import { FeatureResponse, No, Unclear, Unresolved, Yes } from './FeatureResponse';
import { Syndrome } from './Syndrome';
import { defaultSeizure } from './Seizures';
import { hasAnyFeatures } from './Utils';
import { Development } from './Constants';
import { hasOnlyImpairedAtOnset } from './Development';

export class PNPODEE extends Syndrome {
    
    getName() {
        return "PNPODEE";
    }

    checkAgeAndGender(features: Map<string, any>): FeatureResponse {
        let mandatory = super.checkAge(features, 0, 28/365);
        if(mandatory.isYes()) {
            return new Yes("Mandatory present: Age at onset <28 days.");
        }
                
        return new Unclear();
    }

    public checkMandatorySeizures(seizures: any[]): FeatureResponse {
        const annot: string[] = [];

        let s1 = defaultSeizure();
        s1.mainType = "focal";
        s1.onset = "motor";
        s1.details = ["motor_epileptic_spasms"];
        const spasms = this.containsSeizure(seizures, s1, true, annot, "Focal epileptic spasms");

        s1.details = ["motor_myoclonic"];
        const myoclonic = this.containsSeizure(seizures, s1, true, annot, "Focal myoclonic seizures");

        s1 = defaultSeizure();
        s1.details = ["focbilattc"];
        const foc2bilat = this.containsSeizure(seizures, s1, true, annot, "Focal to bilateral tonic-clonic seizures");

        s1 = defaultSeizure();
        s1.other = ["seizure_drug_resistant"];
        const dre = this.containsSeizure(seizures, s1, true, annot, "Drug resistant seizures");

        s1 = defaultSeizure();
        s1.mainType = "generalized";
        s1.onset = "motor";
        s1.details = ["motor_gen_clonic"];
        const gen_clonic = this.containsSeizure(seizures, s1, true, annot, "Generalized clonic seizures");

        s1.details = ["motor_gen_tonic"];
        const gen_tonic = this.containsSeizure(seizures, s1, true, annot, "Generalized tonic seizures");

        s1.details = ["motor_gen_tonic_clonic"];
        const gen_tonic_clonic = this.containsSeizure(seizures, s1, true, annot, "Generalized tonic-clonic seizures");

        if((spasms || myoclonic || gen_clonic || gen_tonic || gen_tonic_clonic) && dre && foc2bilat) {   
            return new Yes(annot);
        } else {
            return new No(annot);
        }
    }

    protected checkDevelopmentDetails(dev: Development[], devAtOnsetAvailable: boolean, devAtCourseAvailable: boolean): FeatureResponse {
        if(!devAtOnsetAvailable) {
            return new Unresolved("Info on development at onset is not available however is critical to determine mandatory features.");
        }
        
        const annot: string[] = [];
        let disability = false;
        if(hasAnyFeatures(dev, ["dev_normal", "dev_disability_ns", "dev_disability_mild", "dev_disability_moderate", "dev_disability_profound"])) {
            annot.push("Mandatory present: Normal development or mild to profound developmental delay (or not further specified).")
            disability = true;
        } else {
            if(hasOnlyImpairedAtOnset(dev)) {
                annot.push("Mandatory missing: Normal development or mild to profound intellectual disability (or not further specified), but impaired development at onset without further details specified, syndrome not ruled out.");
                disability = true;
            } else {
                annot.push("Mandatory missing: Normal development or mild to profound intellectual disability (or not further specified).");
            }   
        }

        if(disability) {
            return new Yes(annot);
        } else {
            return new No(annot);
        }
    }
    
    protected checkHistoryDetails(history: string[], historyAvailable: boolean): FeatureResponse {
        if(!historyAvailable) {
            return new Unresolved("History is not available however is critical to determine mandatory and/or exlusionary features.");
        }

        const annot: string[] = [];
        const mandatoryOk = this.checkFeatures(history, ["history_response_pyr"], true, annot);
                        
        if(mandatoryOk) {
            return new Yes(annot);
        } else {
            return new No(annot);
        }
    }

    protected checkTestsDetails(tests: string[], testsAvailable: boolean): FeatureResponse {
        const annot: string[] = [];
        const geneticTestsAvailable = this.checkFeatures(tests, ["test_genetic"], true, [], true);
        if(!geneticTestsAvailable) {
            annot.push("Genetic testing is not available however should be performed to identify PNPO mutations.");
        }
        if(!testsAvailable) {
            annot.push("Further test results are not available however is critical to determine mandatory decreased pyridoxal-5-phosphate in CSF.");
        }
        if(!testsAvailable || !geneticTestsAvailable) {
            return new Unresolved(annot);
        }

        // laboratory testing: decreased pyridoxal-5-phosphate in CSF
        const mandatoryOk = this.checkFeatures(tests, ["test_lab_decreased_pyr"], true, annot);
        const gene = this.checkFeature(tests, "test_genetics_pnpo", true, annot);
                        
        if(mandatoryOk) {
            if(gene) {
                return new Yes(annot);
            } else {
                if(!geneticTestsAvailable) {
                    return new Yes(annot);
                } else {
                    return new Unclear(annot);
                }
            }
        } else {
            return new No(annot);
        }
    }

    protected checkEEGDetails(eeg: string[], eegAvailable: boolean): FeatureResponse {
        if(!eegAvailable) {
            return new Unresolved("EEG is not available however is critical to determine mandatory and exlusionary features.");
        }

        // abnormal with slowing and focal/multifocal discharges or burst-supression pattern
        const annot: string[] = [];
        const slow = this.checkFeature(eeg, "eeg_slow", true, annot);
        const focal = this.checkFeature(eeg, "eeg_focal", true, annot);
        const multifocal = this.checkFeature(eeg, "eeg_multifocal", true, annot);
        const burstSupp = this.checkFeature(eeg, "eeg_burst_supression", true, annot);
        
        if(slow && (focal || multifocal || burstSupp)) {
            return new Yes(annot);
        } else {
            return new No(annot);
        }
    }

    getAlertCriteria(): string[] {
        return ["Normal neuroimaging", 
            "Unspecific abnormalities on neuroimaging: White matter edema, intraventricular hemorrhage, ventricular dilatation, corpus callosum hypoplasia",
            "Lack of encephalopathy", "Normal neurological examination"];
    }

}